<template>
  <div class="mobile-bottom-nav">
    <!-- Backdrop do Action Sheet -->
    <transition name="fade">
      <div v-if="activeGroup" class="sheet-backdrop" @click="closeSheet"></div>
    </transition>

    <!-- Action Sheet (sobe de baixo) -->
    <transition name="slide-up">
      <div
        v-if="activeGroup"
        ref="sheetRef"
        class="action-sheet"
        :style="{ transform: sheetTransform }"
      >
        <!-- Alça de arrasto + Header: Área de swipe extendida -->
        <div
          class="sheet-swipe-zone"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
        >
          <!-- Alça de arrasto visual -->
          <div class="sheet-drag-handle">
            <div class="drag-bar"></div>
          </div>

          <!-- Header do Sheet -->
          <div class="sheet-header">
            <button class="btn-back" @click="closeSheet">
              <i class="bi bi-chevron-left"></i> Voltar
            </button>
            <span class="sheet-title">
              <i :class="['bi', activeGroup.icon]"></i> {{ activeGroup.label }}
            </span>
          </div>
        </div>

        <!-- Corpo com os itens do grupo -->
        <div class="sheet-body">
          <template v-for="item in activeGroup.items" :key="item.module || item.title">
            <!-- Header visual de seção -->
            <div v-if="item.isHeader" class="sheet-section-header">
              {{ item.title }}
            </div>
            <!-- Item clicável -->
            <button
              v-else
              class="sheet-item"
              :class="{ 'has-color': !!item.colorClass }"
              @click="selectItem(item)"
            >
              <i :class="['bi', item.icon, item.colorClass]"></i>
              <span :class="item.colorClass">{{ item.title }}</span>
            </button>
          </template>
        </div>
      </div>
    </transition>

    <!-- Bottom Tab Bar fixa no rodapé (apenas ícones) -->
    <div class="tab-bar">
      <div class="tab-bar-wrapper">
        <!-- Botão de lupa FIXO (fora do scroll) -->
        <button class="tab-btn tab-btn-search" @click="$emit('abrir-mapa-menus')"
                title="Mapa de Menus">
          <i class="bi bi-search"></i>
        </button>

        <div
          class="tab-bar-inner"
          :class="{
            'tab-bar-distribute': distributeMode,
            'tab-bar-scrollable': !distributeMode
          }"
        >
          <button
            v-for="group in groups"
            :key="group.id"
            class="tab-btn"
            :title="group.label"
            :class="{ 'tab-active': activeGroup?.id === group.id }"
            @click="openSheet(group)"
          >
            <i :class="['bi', group.icon]"></i>
          </button>
        </div>
        <!-- Indicador de mais itens ocultos (modo scrollável) -->
        <div v-if="!distributeMode && groups.length > 0" class="tab-bar-overflow-indicator"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'MobileBottomNav',
  props: {
    menuConfig: {
      type: Array,
      required: true
    }
  },
  emits: ['select-module', 'abrir-mapa-menus'],
  data () {
    return {
      activeGroup: null,
      touchStartY: 0,
      touchCurrentY: 0,
      isDragging: false
    }
  },
  computed: {
    authStore () {
      return useAuthStore()
    },
    isAdminInterno () {
      return this.authStore.isAdminInterno
    },
    groups () {
      return this.menuConfig
        .filter(group => {
          if (group.adminOnly && !this.isAdminInterno) return false
          return group.items.some(item => !item.isHeader && this.checkPerm(item.perm))
        })
        .map(group => ({
          ...group,
          items: this.getVisibleItems(group)
        }))
    },
    /**
     * Até 9 grupos: distribuição uniforme (flex: 1).
     * Acima de 9: modo scrollável com overflow-x.
     */
    distributeMode () {
      return this.groups.length <= 9
    },
    sheetTransform () {
      if (!this.isDragging) return ''
      const delta = this.touchCurrentY - this.touchStartY
      if (delta <= 0) return ''
      return `translateY(${delta}px)`
    }
  },
  methods: {
    checkPerm (permKey) {
      if (!permKey) return true
      if (this.isAdminInterno) return true
      // Unificado: temModulo cobre tanto prefixo de módulo (COMPRAS_*)
      // quanto chave exata (COMPRAS_-_OC_APROVAR)
      return this.authStore.temModulo(permKey)
    },
    getVisibleItems (group) {
      if (group.adminOnly && !this.isAdminInterno) return []
      const items = group.items
      const result = []
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.isHeader) {
          const nextHeaderIdx = items.findIndex((it, idx) => idx > i && it.isHeader)
          const slice = nextHeaderIdx === -1 ? items.slice(i + 1) : items.slice(i + 1, nextHeaderIdx)
          const hasVisible = slice.some(it => !it.isHeader && this.checkPerm(it.perm))
          if (hasVisible) result.push(item)
        } else if (this.checkPerm(item.perm)) {
          result.push(item)
        }
      }
      return result
    },
    openSheet (group) {
      this.activeGroup = group
      this.isDragging = false
      this.touchStartY = 0
      this.touchCurrentY = 0
    },
    closeSheet () {
      this.activeGroup = null
      this.isDragging = false
    },
    selectItem (item) {
      this.$emit('select-module', {
        module: item.module,
        title: item.title,
        item: item
      })
      this.closeSheet()
    },
    onTouchStart (event) {
      this.touchStartY = event.touches[0].clientY
      this.touchCurrentY = this.touchStartY
      this.isDragging = true
    },
    onTouchMove (event) {
      if (!this.isDragging) return
      const delta = event.touches[0].clientY - this.touchStartY
      // Se arrastou para baixo, previne pull-to-refresh da página
      if (delta > 0) {
        event.preventDefault()
      }
      this.touchCurrentY = event.touches[0].clientY
    },
    onTouchEnd () {
      if (!this.isDragging) return
      const delta = this.touchCurrentY - this.touchStartY
      if (delta > 80) {
        this.closeSheet()
      } else {
        this.isDragging = false
        this.touchStartY = 0
        this.touchCurrentY = 0
      }
    }
  }
}
</script>

<style scoped>
/* =============================================
   MOBILE BOTTOM NAV — Container Principal
   ============================================= */
.mobile-bottom-nav {
  display: contents; /* não interfere no layout pai */
}

/* =============================================
   BACKDROP
   ============================================= */
.sheet-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1060;
  backdrop-filter: blur(2px);
}

/* =============================================
   ACTION SHEET
   ============================================= */
.action-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  max-height: 75vh;
  background: var(--bs-body-bg, #fff);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.15);
  z-index: 1070;
  display: flex;
  flex-direction: column;
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* Zona de swipe extendida: alça + header inteiro */
.sheet-swipe-zone {
  flex-shrink: 0;
  touch-action: none; /* essencial para prevenir pull-to-refresh */
}

/* Alça de arrasto */
.sheet-drag-handle {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
  cursor: grab;
  flex-shrink: 0;
}
.drag-bar {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: var(--bs-secondary-color, #adb5bd);
  opacity: 0.5;
}

/* Header do Sheet */
.sheet-header {
  display: flex;
  align-items: center;
  padding: 8px 16px 12px;
  border-bottom: 1px solid var(--bs-border-color-translucent, #dee2e6);
  flex-shrink: 0;
  cursor: grab;
}
.btn-back {
  background: none;
  border: none;
  color: var(--bs-body-color, #212529);
  font-size: 12px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  flex-shrink: 0;
}
.btn-back:hover {
  opacity: 0.7;
}
.sheet-title {
  flex: 1;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  margin-right: 60px; /* compensa o botão Voltar */
}
.sheet-title i {
  margin-right: 6px;
}

/* Corpo do Sheet (scrollável) */
.sheet-body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0 24px;
  flex: 1;
}

/* Header de seção dentro do sheet */
.sheet-section-header {
  padding: 12px 20px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--bs-secondary-color, #6c757d);
}

/* Item do sheet */
.sheet-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 20px;
  border: none;
  background: none;
  color: var(--bs-body-color, #212529);
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.sheet-item:hover,
.sheet-item:active {
  background: var(--bs-tertiary-bg, #f8f9fa);
}
.sheet-item i {
  font-size: 15px;
  margin-right: 12px;
  width: 20px;
  text-align: center;
  color: var(--bs-secondary-color, #495057);
}
.sheet-item span {
  flex: 1;
}

/* =============================================
   BOTTOM TAB BAR
   ============================================= */
.tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 56px;
  background: var(--bs-body-bg, #fff);
  border-top: 1px solid var(--bs-border-color-translucent, #dee2e6);
  z-index: 1050;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}
.tab-bar-wrapper {
  position: relative;
  height: 100%;
}
.tab-bar-inner {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  height: 100%;
  scrollbar-width: none;
  padding-left: 56px;
}

.tab-btn.tab-btn-search {
  position: absolute;
  left: 0;
  top: 0;
  width: 56px;
  height: 100%;
  z-index: 2;
  border: none;
  background: color-mix(in srgb, var(--bs-primary) 12%, var(--bs-body-bg));
  color: var(--bs-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--bs-border-color-translucent);
  flex-shrink: 0;
}

.tab-btn.tab-btn-search i {
  font-size: 18px;
}
.tab-bar-inner::-webkit-scrollbar {
  display: none;
}

/* --- MODO DISTRIBUIDO (≤ 9 grupos) --- */
.tab-bar-distribute {
  justify-content: space-around;
}
.tab-bar-distribute .tab-btn {
  flex: 1 0 0;
  min-width: 0;
  padding: 8px 4px;
}

/* --- MODO SCROLLÁVEL (> 9 grupos) --- */
.tab-bar-scrollable {
  justify-content: flex-start;
}
.tab-bar-scrollable .tab-btn {
  flex: 0 0 auto;
  min-width: 48px;
  padding: 8px 14px;
}

/* Indicador de overflow (fade gradiente na borda direita) */
.tab-bar-overflow-indicator {
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(
    to right,
    transparent 0%,
    var(--bs-body-bg, #fff) 100%
  );
  opacity: 0.85;
}

/* Botão base da tab */
.tab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: var(--bs-secondary-color, #6c757d);
  cursor: pointer;
  transition: color 0.2s ease;
}
.tab-btn i {
  font-size: 18px;
  line-height: 1;
}
.tab-btn.tab-active {
  color: var(--bs-primary, #0d6efd);
  position: relative;
}
.tab-btn.tab-active::after {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 3px;
  border-radius: 0 0 3px 3px;
  background: var(--bs-primary, #0d6efd);
}

/* =============================================
   TRANSIÇÕES
   ============================================= */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.slide-up-leave-active {
  transition: transform 0.25s ease-in;
}
.slide-up-enter-from {
  transform: translateY(100%);
}
.slide-up-leave-to {
  transform: translateY(100%);
}
.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
}

/* =============================================
   DARK MODE AJUSTES
   ============================================= */
[data-bs-theme='dark'] .tab-bar {
  border-top-color: var(--bs-border-color-translucent, #2d2d2d);
}
[data-bs-theme='dark'] .sheet-item:hover,
[data-bs-theme='dark'] .sheet-item:active {
  background: var(--bs-tertiary-bg, #2d2d2d);
}
[data-bs-theme='dark'] .tab-bar-overflow-indicator {
  background: linear-gradient(
    to right,
    transparent 0%,
    var(--bs-body-bg, #212529) 100%
  );
}
</style>
